import { NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useMerchantAuth } from "@/lib/auth";
import Brand from "@/components/Brand";
import DashboardShell from "@/components/DashboardShell";
import { LayoutDashboard, Package, Tags, ShoppingCart, Users, Settings, FileText, LogOut, ExternalLink, Ticket, MessageCircle, Smartphone, Receipt, Search, Puzzle, Wallet, ImageIcon, Palette, Globe, Newspaper, MapPin, Truck, Wheat, Mail, GraduationCap, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { storePublicBase } from "@/lib/api";
import RenewGate from "./RenewGate";
import { useTranslation } from "react-i18next";
import LocaleProvider from "@/i18n/LocaleProvider";
import MerchantSupportAi from "@/components/MerchantSupportAi";
import MerchantNoticePopup from "@/components/MerchantNoticePopup";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, key: "nav.dashboard", testId: "nav-dashboard", end: true },
  { to: "/admin/refer", icon: Wallet, key: "nav.referWallet", testId: "nav-refer-wallet" },
  { to: "/admin/products", icon: Package, key: "nav.products", testId: "nav-products" },
  { to: "/admin/categories", icon: Tags, key: "nav.categories", testId: "nav-categories" },
  { to: "/admin/orders", icon: ShoppingCart, key: "nav.orders", testId: "nav-orders" },
  { to: "/admin/coupons", icon: Ticket, key: "nav.coupons", testId: "nav-coupons" },
  { to: "/admin/customers", icon: Users, key: "nav.customers", testId: "nav-customers" },
  { to: "/admin/pages", icon: FileText, key: "nav.pages", testId: "nav-pages" },
  { to: "/admin/seo", icon: Search, key: "nav.seo", testId: "nav-seo" },
  { to: "/admin/media", icon: ImageIcon, key: "nav.media", testId: "nav-media" },
  { to: "/admin/settings", icon: Settings, key: "nav.settings", testId: "nav-settings" },
  { to: "/admin/appearance", icon: Palette, key: "nav.appearance", testId: "nav-appearance" },
  { to: "/admin/support", icon: MessageCircle, key: "nav.support", testId: "nav-support", badgeKey: "support" },
  { to: "/admin/my-application", icon: Smartphone, key: "nav.myApplication", testId: "nav-my-application" },
  { to: "/admin/addons", icon: Puzzle, key: "nav.appStore", testId: "nav-addons" },
  { to: "/admin/billing", icon: Receipt, key: "nav.billing", testId: "nav-billing" },
  { to: "/admin/domain", icon: Globe, key: "nav.myDomain", testId: "nav-my-domain" },
];

function AdminShell() {
  const { user, tenant, logout } = useMerchantAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const { t } = useTranslation();
  const storeEnabled = tenant?.store_enabled !== false && tenant?.account_kind !== "domain_only";
  const hasDomain = Boolean(tenant?.has_domain || tenant?.account_kind === "domain_only");
  const [supportUnread, setSupportUnread] = useState(0);
  const [walletBal, setWalletBal] = useState(null);
  const [blogOn, setBlogOn] = useState(false);
  const [odAreas, setOdAreas] = useState(false);
  const [odDelivery, setOdDelivery] = useState(false);
  const [buildBoxOn, setBuildBoxOn] = useState(false);
  const [abpOn, setAbpOn] = useState(false);
  const [contactFormOn, setContactFormOn] = useState(false);
  const [coursePagesOn, setCoursePagesOn] = useState(false);
  const [authorsOn, setAuthorsOn] = useState(false);
  const visibleNav = (() => {
    let items = storeEnabled
      ? (hasDomain ? NAV : NAV.filter((n) => n.to !== "/admin/domain"))
      : NAV.filter((n) => n.to === "/admin/domain");
    if (storeEnabled && blogOn && !items.some((n) => n.to === "/admin/blogs")) {
      const blogs = { to: "/admin/blogs", icon: Newspaper, key: "nav.blogs", testId: "nav-blogs" };
      const idx = items.findIndex((n) => n.to === "/admin/pages");
      items = [...items];
      if (idx >= 0) items.splice(idx + 1, 0, blogs);
      else items.push(blogs);
    }
    if (storeEnabled && odAreas && !items.some((n) => n.to === "/admin/ondemand/areas")) {
      items = [...items, { to: "/admin/ondemand/areas", icon: MapPin, key: "nav.ondemandAreas", testId: "nav-ondemand-areas", label: "Service area" }];
    }
    if (storeEnabled && odDelivery && !items.some((n) => n.to === "/admin/ondemand/riders")) {
      items = [...items, { to: "/admin/ondemand/riders", icon: Truck, key: "nav.ondemandRiders", testId: "nav-ondemand-riders", label: "Delivery boys" }];
    }
    if (storeEnabled && abpOn && !items.some((n) => n.to === "/admin/abp")) {
      const abpNav = { to: "/admin/abp", icon: Wheat, key: "nav.abp", testId: "nav-abp", label: "ABP Capabilities" };
      const idx = items.findIndex((n) => n.to === "/admin/products");
      items = [...items];
      if (idx >= 0) items.splice(idx + 1, 0, abpNav);
      else items.push(abpNav);
    }
    if (storeEnabled && authorsOn && !items.some((n) => n.to === "/admin/authors")) {
      const authorNav = { to: "/admin/authors", icon: PenLine, key: "nav.authors", testId: "nav-authors", label: "Authors" };
      const idx = items.findIndex((n) => n.to === "/admin/products");
      items = [...items];
      if (idx >= 0) items.splice(idx + 1, 0, authorNav);
      else items.push(authorNav);
    }
    if (storeEnabled && coursePagesOn && !items.some((n) => n.to === "/admin/course-pages")) {
      const courseNav = { to: "/admin/course-pages", icon: GraduationCap, key: "nav.coursePage", testId: "nav-course-pages", label: "Course Page" };
      const idx = items.findIndex((n) => n.to === "/admin/pages");
      items = [...items];
      if (idx >= 0) items.splice(idx + 1, 0, courseNav);
      else items.push(courseNav);
    }
    if (storeEnabled && contactFormOn && !items.some((n) => n.to === "/admin/contact-form")) {
      const formNav = { to: "/admin/contact-form", icon: Mail, key: "nav.contactForm", testId: "nav-contact-form", label: "Contact form" };
      const idx = items.findIndex((n) => n.to === "/admin/pages");
      items = [...items];
      if (idx >= 0) items.splice(idx + 1, 0, formNav);
      else items.push(formNav);
    }
    if (storeEnabled && buildBoxOn && !items.some((n) => n.to === "/admin/build-boxes")) {
      const boxNav = { to: "/admin/build-boxes", icon: Package, key: "nav.buildBox", testId: "nav-build-boxes", label: "Build a box" };
      const idx = items.findIndex((n) => n.to === "/admin/products");
      items = [...items];
      if (idx >= 0) items.splice(idx + 1, 0, boxNav);
      else items.push(boxNav);
    }
    return items;
  })();
  const openStorefront = async (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
    e.preventDefault();
    const fallback = storePublicBase(tenant);
    const tab = window.open("about:blank", "_blank");
    try {
      const { data } = await api.post("/auth/merchant/storefront-preview");
      const url = data.url || fallback;
      if (tab) tab.location.replace(url);
      else window.location.assign(url);
    } catch {
      if (tab) tab.location.replace(fallback);
      else window.location.assign(fallback);
    }
  };
  const doLogout = async () => {
    await logout();
    toast.success(t("common.signedOut"));
    nav("/login");
  };

  useEffect(() => {
    api.get("/merchant/config").then(({ data }) => {
      setBlogOn(Boolean(data?.store_blog_active));
      setOdAreas(Boolean(data?.ondemand_areas_active));
      setOdDelivery(Boolean(data?.ondemand_delivery_active));
      setBuildBoxOn(Boolean(data?.build_your_box_active));
      setAbpOn(Boolean(data?.abp_capabilities_active));
      setContactFormOn(Boolean(data?.contact_page_form_active));
      setCoursePagesOn(Boolean(data?.course_page_maker_active));
      setAuthorsOn(Boolean(data?.author_pages_active));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    const tick = () => {
      api.get("/merchant/support/unread_count").then(({ data }) => { if (alive) setSupportUnread(data.unread || 0); }).catch(() => {});
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    let alive = true;
    const loadBal = () => {
      api.get("/merchant/wallet/summary").then(({ data }) => { if (alive) setWalletBal(data.balance); }).catch(() => {});
    };
    loadBal();
    const id = setInterval(loadBal, 60000);
    const onUp = (e) => { if (alive && typeof e.detail === "number") setWalletBal(e.detail); };
    window.addEventListener("wallet-updated", onUp);
    return () => { alive = false; clearInterval(id); window.removeEventListener("wallet-updated", onUp); };
  }, []);

  const WalletChip = ({ compact }) => (
    <NavLink
      to="/admin/refer"
      data-testid="wallet-chip"
      className={`inline-flex items-center gap-1.5 rounded-full bg-amber-400 text-slate-900 border border-amber-500 shadow-md hover:bg-amber-300 font-bold font-mono-nums ${compact ? "px-2.5 py-1 text-[11px]" : "px-3.5 py-1.5 text-sm"}`}
    >
      <Wallet className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} text-slate-900`} />
      {walletBal == null ? "Wallet" : `₹${Number(walletBal).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
    </NavLink>
  );

  if (tenant?.subscription?.expired) {
    return <RenewGate />;
  }

  if (!storeEnabled && loc.pathname !== "/admin/domain" && loc.pathname.startsWith("/admin")) {
    return <Navigate to="/admin/domain" replace />;
  }

  const Sidebar = ({ onNav }) => (
    <div className="w-64 bg-slate-900 text-slate-100 flex flex-col h-full">
      <div className="p-5 border-b border-white/5">
        <Brand variant="sidebar" size="sm" className="max-w-full" />
        {tenant && (
          <div className="mt-4 p-3 rounded-lg bg-slate-800/60 border border-white/5">
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">{t("nav.store")}</div>
            <div className="mt-0.5 font-display font-semibold text-sm truncate">{tenant.name}</div>
            {storeEnabled ? (
            <a href={storePublicBase(tenant)} onClick={openStorefront} target="_blank" rel="noopener noreferrer" data-testid="admin-view-store" className="mt-1 inline-flex items-center gap-1 text-xs text-orange-300 hover:text-orange-200 font-mono-nums break-all">
              {tenant.custom_domain && tenant.custom_domain_verified
                ? tenant.custom_domain
                : `/${tenant.slug}`} <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
            ) : (
              <div className="mt-1 text-xs text-orange-300 font-mono-nums break-all">{tenant.custom_domain || "Domain account"}</div>
            )}
          </div>
        )}
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto dash-scroll-dark">
        {visibleNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testId}
            onClick={onNav}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <n.icon className="w-4 h-4" />
            <span className="flex-1">{n.label || t(n.key)}</span>
            {n.badgeKey === "support" && supportUnread > 0 && (
              <span className="min-w-[20px] h-5 rounded-full bg-orange-500 text-slate-900 text-[11px] font-bold flex items-center justify-center px-1.5" data-testid="nav-support-badge">{supportUnread}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/5">
        <div className="px-3 py-2 text-xs text-slate-400 truncate">{user?.email}</div>
        <Button variant="ghost" size="sm" onClick={doLogout} data-testid="admin-logout" className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/5">
          <LogOut className="w-4 h-4 mr-2" /> {t("nav.signOut")}
        </Button>
      </div>
    </div>
  );

  return (
    <DashboardShell
      menuTestId="admin-mobile-menu"
      renderSidebar={(onNav) => <Sidebar onNav={onNav} />}
      brand={<Brand variant="dark" size="sm" className="max-h-8 max-w-[150px] overflow-hidden" />}
      trailing={storeEnabled ? <WalletChip compact /> : null}
      desktopHeader={storeEnabled ? (
        <div className="hidden md:flex sticky top-0 z-20 justify-end px-6 pt-4 pb-1 bg-gradient-to-b from-slate-50 via-slate-50 to-transparent pointer-events-none">
          <div className="pointer-events-auto"><WalletChip /></div>
        </div>
      ) : null}
      contentClassName="p-4 sm:p-6 md:p-8 md:pt-2 max-w-full"
      overlay={storeEnabled ? <><MerchantNoticePopup /><MerchantSupportAi /></> : <MerchantNoticePopup />}
    >
      <Outlet />
    </DashboardShell>
  );
}

export default function AdminLayout() {
  const [lng, setLng] = useState("en");
  useEffect(() => {
    api.get("/merchant/config").then(({ data }) => setLng(data?.i18n?.dashboard_language || "en")).catch(() => {});
    const onLang = (e) => setLng(e.detail || "en");
    window.addEventListener("dashboard-lang", onLang);
    return () => window.removeEventListener("dashboard-lang", onLang);
  }, []);
  return (
    <LocaleProvider lng={lng}>
      <AdminShell />
    </LocaleProvider>
  );
}
